import { useState } from "react";
import { Link } from "react-router";
import { Badge } from "../../primitives/badge";
import type { SalesOrderNeedingAttentionItem } from "@shared-core/data/api-types";
import { plural } from "@shared-core/domain/deadline";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import {
  BOARD_SECTION_PREVIEW_COUNT,
  ExpandableSection,
  SEVERITY_DOT,
} from "./BoardSectionShell";
import { currentTime } from "@shared-core/domain/current-time";

/** Whole days between `orderDate` and now, floored at 0 — an order placed
 *  earlier today reads as "0 days" rather than a confusing negative. Not a
 *  generic formatter in `domain/formatters.ts`: this "age since an order
 *  was placed" framing is specific to this section. */
function formatAgeInDays(orderDate: string): string {
  const days = Math.max(
    0,
    Math.floor(
      (currentTime().getTime() - new Date(orderDate).getTime()) / 86_400_000,
    ),
  );
  return `${days} ${plural("day", days)}`;
}

/**
 * Confirmed Tharstern sales orders that are not (fully) allocated — see
 * docs/features/sales-orders-needing-attention/01_design.md. CSR-home only:
 * this is never shown to the customer (allocation state is internal
 * operational detail), and there is no CSR-facing orders tab yet, so each
 * row links to the customer detail page rather than a specific order view.
 * Shares `PortfolioBoard`'s section chrome and severity-dot convention but is
 * a distinct feature (order allocation), so it is its own section rather
 * than folded into `PortfolioBoard`.
 */
export function SalesOrdersNeedingAttentionSection({
  orders,
  totalCount,
}: {
  orders: SalesOrderNeedingAttentionItem[];
  /** The true total behind the (server-capped) `orders` array — see
   *  `ExpandableSection`'s `totalCount` prop. */
  totalCount: number;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <ExpandableSection
      title="Sales orders needing attention"
      items={orders}
      totalCount={totalCount}
      emptyMessage="No sales orders need attention."
      previewCount={BOARD_SECTION_PREVIEW_COUNT}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((current) => !current)}
      renderItem={(order) => (
        <SalesOrderNeedingAttentionRow key={order.order_no} order={order} />
      )}
    />
  );
}

function SalesOrderNeedingAttentionRow({
  order,
}: {
  order: SalesOrderNeedingAttentionItem;
}) {
  return (
    <Link
      to={order.self_url}
      className="flex items-start gap-2 p-3 border border-border rounded-lg bg-card hover:bg-surface-hover transition-colors text-sm"
    >
      {/* Every row here is action-needed, so amber is the only severity this
          row ever shows. */}
      <span
        className={`mt-1.5 size-2.5 rounded-full shrink-0 ${SEVERITY_DOT.amber}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-0.5">
          <span className="font-medium truncate">
            {order.customer_name ?? order.account_code} — Order {order.order_no}
          </span>
          <Badge variant="warning" className="shrink-0 uppercase">
            {order.allocation_status_label}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span>{formatAgeInDays(order.order_date)} old</span>
          {order.customer_ref && <span>Ref {order.customer_ref}</span>}
          <span>{formatCurrencyGbp(Number(order.net_amount), 2)}</span>
          <span>
            {order.job_nos.length === 0
              ? "No job"
              : `Job${order.job_nos.length > 1 ? "s" : ""} ${order.job_nos.join(", ")}`}
          </span>
        </div>
      </div>
    </Link>
  );
}
